import { supabase } from '@/integrations/supabase/client';

interface ProgressUpdate {
  processed: number;
  successful: number;
  failed: number;
  total: number;
  percentage: number;
}

export class AudioGenerationWorker {
  private operationId: string | null = null;
  private isRunning: boolean = false;
  private pollInterval: number | null = null;

  async start(onProgress?: (progress: ProgressUpdate) => void): Promise<void> {
    if (this.isRunning) {
      console.log('⚠️ Audio generation already running');
      return;
    }

    try {
      this.isRunning = true;

      // Start server-side processing
      const { data, error } = await supabase.functions.invoke('autonomous-audio-generator', {
        body: { action: 'start' }
      });

      if (error) throw error;

      if (!data.success) {
        throw new Error(data.error || 'Failed to start audio generation');
      }

      this.operationId = data.operationId;
      console.log(`🎵 Server-side audio generation started: ${this.operationId}`);
      console.log(`📊 Total modules to process: ${data.totalModules}`);
      console.log(`⏰ Cron will process ~10 modules every 3 minutes (~200/hour)`);
      console.log(`🎯 Estimated completion: ~${Math.ceil(data.totalModules / 200)} hours`);

      // Poll for progress updates every 5 seconds
      this.pollInterval = window.setInterval(async () => {
        await this.checkProgress(onProgress);
      }, 5000);

      // Initial progress check
      await this.checkProgress(onProgress);

    } catch (error: any) {
      console.error('Failed to start audio generation:', error);
      this.isRunning = false;
      throw error;
    }
  }

  private async checkProgress(onProgress?: (progress: ProgressUpdate) => void): Promise<void> {
    if (!this.operationId) return;

    try {
      const { data: operation, error } = await supabase
        .from('audio_generation_operations')
        .select('*')
        .eq('id', this.operationId)
        .single();

      if (error) throw error;

      const processed = operation.processed_items || 0;
      const successful = operation.successful_items || 0;
      const failed = operation.failed_items || 0;
      const total = operation.total_items || 0;
      const percentage = total > 0 ? Math.round((processed / total) * 100) : 0;

      const progress: ProgressUpdate = {
        processed,
        successful,
        failed,
        total,
        percentage
      };

      onProgress?.(progress);

      // Check if complete
      if (operation.status === 'completed') {
        this.stop();
        console.log(`✅ Audio generation completed: ${successful}/${total} successful, ${failed} failed`);
      } else if (operation.status === 'cancelled') {
        this.stop();
        console.log(`⏹️ Audio generation cancelled at ${processed}/${total}`);
      } else if (operation.status === 'failed') {
        this.stop();
        console.error(`❌ Audio generation failed: ${operation.error_message}`);
      }

    } catch (error) {
      console.error('Error checking progress:', error);
    }
  }

  async stop(): Promise<void> {
    if (!this.isRunning) return;

    try {
      // Clear polling interval
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }

      // Send stop signal to server
      if (this.operationId) {
        await supabase.functions.invoke('autonomous-audio-generator', {
          body: { action: 'stop' }
        });
      }

      this.isRunning = false;
      this.operationId = null;
      console.log('⏹️ Audio generation stopped');

    } catch (error) {
      console.error('Error stopping audio generation:', error);
    }
  }

  getStatus() {
    return {
      isRunning: this.isRunning,
      operationId: this.operationId
    };
  }

  async getServerStatus() {
    try {
      const { data, error } = await supabase.functions.invoke('autonomous-audio-generator', {
        body: { action: 'check-status' }
      });

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error getting server status:', error);
      return null;
    }
  }
}

export const audioWorker = new AudioGenerationWorker();
